import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { api } from '@consts/url.const';

export interface AnalyticsSyncLogEntry {
  id: number;
  orgId: string;
  orgIdHash: number;
  customObjectsCount: number;
  customObjectsRepresentativesCount: number;
  personsCount: number;
  personsRepresentativesCount: number;
  personRepresentativesNegativeCount: number;
  edgeIds: string[];
  edgesCount: number;
  shelvesCount: number;
  errorMessage?: string;
  createdAt: Date;
}

export interface AnalyticsSyncStats {
  totalSyncs: number;
  syncsWithErrors: number;
  avgEdgesPerSync: number;
}

export interface AnalyticsSyncResponse {
  edgesCount: number;
  orgId: string;
  customObjectsCount: number;
  customObjectsRepresentativesCount: number;
  personsCount: number;
  personsRepresentativesCount: number;
  personRepresentativesNegativeCount: number;
  shelvesCount?: number;
  presignedUrl: string;
}

export interface TriggerSyncRequest {
  // Add any required fields for the trigger sync request
  // Based on the backend, it might be empty or contain specific parameters
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsSyncService {
  constructor(private http: HttpClient) {}

  /**
   * Trigger analytics sync to all edges
   */
  triggerSync(request: TriggerSyncRequest = {}): Observable<AnalyticsSyncResponse> {
    return this.http.post<AnalyticsSyncResponse>(api.analyticsSync.triggerSync, request).pipe(
      map(response => ({
        ...response,
        // Ensure all required fields are present
        edgesCount: response.edgesCount || 0,
        customObjectsCount: response.customObjectsCount || 0,
        customObjectsRepresentativesCount: response.customObjectsRepresentativesCount || 0,
        personsCount: response.personsCount || 0,
        personsRepresentativesCount: response.personsRepresentativesCount || 0,
        personRepresentativesNegativeCount: response.personRepresentativesNegativeCount || 0,
        presignedUrl: response.presignedUrl || ''
      })),
      catchError(error => {
        console.error('Failed to trigger analytics sync:', error);
        throw error;
      })
    );
  }

  /**
   * Get analytics sync logs for the organization
   */
  getSyncLogs(limit: number = 20): Observable<AnalyticsSyncLogEntry[]> {
    const params = { limit: limit.toString() };

    return this.http.get<AnalyticsSyncLogEntry[]>(api.analyticsSync.logs, { params }).pipe(
      map(logs => logs.map(log => ({
        ...log,
        createdAt: new Date(log.createdAt),
        edgeIds: Array.isArray(log.edgeIds) ? log.edgeIds : [],
        edgesCount: log.edgesCount || log.edgeIds?.length || 0
      }))),
      catchError(error => {
        console.error('Failed to get sync logs:', error);
        throw error;
      })
    );
  }

  /**
   * Get analytics sync statistics for the organization
   */
  getSyncStats(): Observable<AnalyticsSyncStats> {
    return this.http.get<AnalyticsSyncStats>(api.analyticsSync.stats).pipe(
      map(stats => ({
        totalSyncs: stats.totalSyncs || 0,
        syncsWithErrors: stats.syncsWithErrors || 0,
        avgEdgesPerSync: stats.avgEdgesPerSync || 0
      })),
      catchError(error => {
        console.error('Failed to get sync stats:', error);
        throw error;
      })
    );
  }

  /**
   * Get detailed information about a specific sync log entry
   */
  getSyncLogDetails(logId: number): Observable<AnalyticsSyncLogEntry> {
    return this.http.get<AnalyticsSyncLogEntry>(api.analyticsSync.logDetails(logId)).pipe(
      map(log => ({
        ...log,
        createdAt: new Date(log.createdAt),
        edgeIds: Array.isArray(log.edgeIds) ? log.edgeIds : [],
        edgesCount: log.edgesCount || log.edgeIds?.length || 0
      })),
      catchError(error => {
        console.error('Failed to get sync log details:', error);
        throw error;
      })
    );
  }

  /**
   * Check if analytics sync is currently in progress
   * This could be enhanced to check actual sync status from the backend
   */
  getSyncStatus(): Observable<{ inProgress: boolean; lastSync?: Date }> {
    // For now, return a simple status
    // In a real implementation, this might check the backend for active sync operations
    return new Observable(observer => {
      observer.next({ inProgress: false });
      observer.complete();
    });
  }
}
